// Per-dialect synthesizer for CREATE / ALTER / DROP LOGIN (v2 Slice F1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice F1.
//
// Logins are an mssql-specific concept — server-level principals that
// authenticate connections, distinct from db-level USER objects. Every other
// engine throws with a documented redirect to `create-user` / `create-role`.

import type { SqlEngine } from '../../../sql-grammar/model';
import type { LoginChange, LoginDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent, quoteSqlLiteral } from './dialect';

const NON_MSSQL_HINT
  = `LOGIN is an mssql-only construct (server-level principal). Other `
    + `engines collapse server + database principals into a single user/role `
    + `— use op:'create-user' or op:'create-role' instead.`;

function rejectIfNotMssql(engine: SqlEngine, op: string): void {
  if (engine !== 'mssql') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: ${engine} does not support ${op} — ${NON_MSSQL_HINT}`,
    );
  }
}

// ─── CREATE LOGIN ─────────────────────────────────────────────────────────

export function synthesizeCreateLogin(engine: SqlEngine, login: LoginDef): string {
  rejectIfNotMssql(engine, 'create-login');
  if (login.mssqlPassword !== undefined && login.mssqlFromWindows) {
    throw new Error(
      `applyDdlEdit: mssql create-login cannot set both 'mssqlPassword' and `
        + `'mssqlFromWindows' — they are mutually exclusive authentication kinds.`,
    );
  }
  if (login.mssqlPassword === undefined && !login.mssqlFromWindows) {
    throw new Error(
      `applyDdlEdit: mssql create-login requires one of 'mssqlPassword' (SQL `
        + `auth) or 'mssqlFromWindows: true' (Windows auth).`,
    );
  }
  const name = maybeQuoteIdent(engine, login.name, login.quoted ?? false);
  if (login.mssqlFromWindows) {
    let s = `CREATE LOGIN ${name} FROM WINDOWS`;
    const withParts: string[] = [];
    if (login.mssqlDefaultDatabase !== undefined) {
      withParts.push(`DEFAULT_DATABASE = ${login.mssqlDefaultDatabase}`);
    }
    if (withParts.length > 0) s += ` WITH ${withParts.join(', ')}`;
    return s;
  }
  // SQL auth
  const withParts: string[] = [`PASSWORD = ${quoteSqlLiteral(login.mssqlPassword ?? '')}`];
  if (login.mssqlDefaultDatabase !== undefined) {
    withParts.push(`DEFAULT_DATABASE = ${login.mssqlDefaultDatabase}`);
  }
  if (login.mssqlCheckPolicy !== undefined) {
    withParts.push(`CHECK_POLICY = ${login.mssqlCheckPolicy ? 'ON' : 'OFF'}`);
  }
  if (login.mssqlCheckExpiration !== undefined) {
    withParts.push(`CHECK_EXPIRATION = ${login.mssqlCheckExpiration ? 'ON' : 'OFF'}`);
  }
  return `CREATE LOGIN ${name} WITH ${withParts.join(', ')}`;
}

// ─── ALTER LOGIN ──────────────────────────────────────────────────────────

export function synthesizeAlterLogin(
  engine: SqlEngine,
  loginName: string,
  quoted: boolean,
  change: LoginChange,
): string {
  rejectIfNotMssql(engine, 'alter-login');
  const {
    mssqlPassword,
    mssqlOldPassword,
    mssqlDefaultDatabase,
    mssqlEnable,
    mssqlName,
  } = change;
  if (mssqlOldPassword !== undefined && mssqlPassword === undefined) {
    throw new Error(
      `applyDdlEdit: alter-login 'mssqlOldPassword' requires 'mssqlPassword' — `
        + `OLD_PASSWORD is meaningful only paired with a new PASSWORD.`,
    );
  }
  if (
    mssqlPassword === undefined
    && mssqlDefaultDatabase === undefined
    && mssqlEnable === undefined
    && mssqlName === undefined
  ) {
    throw new Error(
      `applyDdlEdit: alter-login requires at least one change field — `
        + `mssqlPassword / mssqlDefaultDatabase / mssqlEnable / mssqlName.`,
    );
  }
  const name = maybeQuoteIdent(engine, loginName, quoted);
  // Bare ENABLE / DISABLE — mssql treats this as its own statement shape.
  if (mssqlEnable !== undefined) {
    return `ALTER LOGIN ${name} ${mssqlEnable ? 'ENABLE' : 'DISABLE'}`;
  }
  const withParts: string[] = [];
  if (mssqlPassword !== undefined) {
    let p = `PASSWORD = ${quoteSqlLiteral(mssqlPassword)}`;
    if (mssqlOldPassword !== undefined) p += ` OLD_PASSWORD = ${quoteSqlLiteral(mssqlOldPassword)}`;
    withParts.push(p);
  }
  if (mssqlDefaultDatabase !== undefined) {
    withParts.push(`DEFAULT_DATABASE = ${mssqlDefaultDatabase}`);
  }
  if (mssqlName !== undefined) {
    withParts.push(`NAME = ${mssqlName}`);
  }
  return `ALTER LOGIN ${name} WITH ${withParts.join(', ')}`;
}

// ─── DROP LOGIN ───────────────────────────────────────────────────────────

export function synthesizeDropLogin(
  engine: SqlEngine,
  loginName: string,
  quoted: boolean,
  ifExists: boolean,
): string {
  rejectIfNotMssql(engine, 'drop-login');
  const name = maybeQuoteIdent(engine, loginName, quoted);
  let s = 'DROP LOGIN';
  if (ifExists) s += ' IF EXISTS';
  s += ` ${name}`;
  return s;
}
